import {
  ToastStatus,
  useIsMsalAuth,
} from '@equifinance/shared';
import useApplicationApi from '../../api/useApplicationApi';
import useTransitionResult from '../account/useTransitionResult';
import { ErrorDisplayType, TransitionError } from '../account';
import { Application, ApplicationStageBackward, TransitionBackwardResponse } from './Application';

function errorsByDisplayType(errors: TransitionError[], dt: ErrorDisplayType) {
  return errors?.filter(({ displayType }) => displayType === dt);
}

export function useTransitionBackward(
  updateApplication: (id: string, data: Partial<Application>) => void,
) {
  const { putChangeApplicationStatus } = useApplicationApi();

  const isMsalAuth = useIsMsalAuth();
  const contactTeam = isMsalAuth ? 'Equifinance IT Team' : 'Equifinance';

  const {
    showSuccessModal,
    showWarningModal,
    showFailureModal,
    showTransitionToast,
  } = useTransitionResult();

  const handleTransitionResponse = (result: TransitionBackwardResponse) => {
    const { errors, warnings } = result.reason ?? {};

    const hasErrors = errors?.length;
    const hasWarnings = warnings?.length;
    const success = !hasErrors;

    if (success) {
      const title = 'Status change success';

      if (!hasWarnings) {
        showSuccessModal(title, `${title}.`, []);
        return;
      }

      const content = 'Status change completed with warnings, please see details below:';
      const warningsModal = errorsByDisplayType(warnings, ErrorDisplayType.Modal);
      const warningsToast = errorsByDisplayType(warnings, ErrorDisplayType.Toast);

      if (warningsModal.length) {
        showWarningModal(title, content, warningsModal);
      }

      if (warningsToast.length) {
        showTransitionToast(ToastStatus.WARNING, content, warningsToast);
      }

      return;
    }

    const title = 'Status change failure';
    const label = `Status change failure, please see details below. If the issue persists please contact ${contactTeam} for further support:`;
    const errorsModal = errorsByDisplayType(errors, ErrorDisplayType.Modal);
    const errorsToast = errorsByDisplayType(errors, ErrorDisplayType.Toast);

    if (errorsModal.length) {
      showFailureModal(title, label, errorsModal);
    }

    if (errorsToast.length) {
      showTransitionToast(ToastStatus.ERROR, label, errorsToast);
    }
  };

  const makeTransitionBackward = async (id: string, stage: ApplicationStageBackward) => {
    const result = await putChangeApplicationStatus(id, stage);
    updateApplication(id, result);
    handleTransitionResponse(result);
  };

  return { makeTransitionBackward };
}
